import { CropFieldAdvice } from '@next/arable-lib/advice';
import { CropVariety } from '@next/arable-lib/crop';
import { CropField } from '@next/arable-lib/crop-field';
import { createHttpActions } from '@next/arable-lib/shared';
import { createAction, props } from '@ngrx/store';

import {
  CropAid,
  CropAidPackage,
  CustomerOrderPlan,
  FertilizerOrderPlanResponse,
  FertilizerStockData,
  GetAutomaticAdviceResponse,
  GetCompanySettingsResponse,
  ManualCrop,
  OrderLine,
  Package,
  ProductStockData,
  SalesOrderPeriod,
} from '../../shared/models';

export const [loadCompanySettings, loadCompanySettingsSuccess, loadCompanySettingsFailure] = createHttpActions<
  { year: number },
  { companySettings: GetCompanySettingsResponse },
  { message: string }
>('Load company settings');

export const loadCropVarietiesByCropIdRequest = createAction(
  '[ManualCrops][CropVariety] Load crop varieties by crop id request',
  props<{ cropIds: number[] }>(),
);

export const loadCropVarietiesByCropIdSuccess = createAction(
  '[ManualCrops][CropVariety] Load crop varieties by crop id success',
  props<{ cropIds: number[]; cropVarieties: { cropId: number; cropVarieties: CropVariety[] }[] }>(),
);

export const [loadAutomaticAdvice, loadAutomaticAdviceSuccess, loadAutomaticAdviceFailure] = createHttpActions<
  { cropYear: number },
  { automaticAdvice: GetAutomaticAdviceResponse | undefined },
  { message: string }
>('[AutomaticAdvice][API] Load automatic advice');

export const deleteCropField = createAction(
  '[ManualCrops][Delete] Delete crop field',
  props<{ cropFieldId: number }>(),
);

export const deleteCropFieldSuccess = createAction(
  '[ManualCrops][Delete] Delete crop field success',
  props<{ cropFieldId: number }>(),
);

export const deleteCropFieldFailed = createAction('[ManualCrops][Delete] Delete crop field failed');

export const upsertCropField = createAction(
  '[ManualCrops][Crop Field] Upsert Crop Field',
  props<{ cropField: ManualCrop }>(),
);

export const upsertCropFieldSuccess = createAction('[ManualCrops][Crop Field] Upsert Crop Field Success');

export const upsertCropFieldFailed = createAction('[ManualCrops][Crop Field] Upsert Crop Field Failed');

export const [loadExistingCropFields, loadExistingCropFieldsSuccess, loadExistingCropFieldsFailure] = createHttpActions<
  { cropYear: number },
  { existingCropFields: CropField[] },
  { message: string }
>('Load existing crop fields');

export const [loadCropFieldAdvices, loadCropFieldAdvicesSuccess, loadCropFieldAdvicesFailure] = createHttpActions<
  { cropYear: number },
  { cropFieldAdvices: CropFieldAdvice[] },
  { message: string }
>('Load crop field advices');

export const [
  loadHasAnyFinalizedOrderLines,
  loadHasAnyFinalizedOrderLinesSuccess,
  loadHasAnyFinalizedOrderLinesFailure,
] = createHttpActions<{ year: number }, { hasAnyFinalizedOrderLines: boolean }, { message: string }>(
  '[ManualCrops][OrderPlan] Load has any finalized order lines',
);

export const [loadCustomerOrderPlan, loadCustomerOrderPlanSuccess, loadCustomerOrderPlanFailure] = createHttpActions<
  void,
  { customerOrderPlan: CustomerOrderPlan[] },
  { message: string }
>('[ManualCrops][OrderPlan] Load customer order plan');

export const editCustomerProductStock = createAction('[ManualCrops][OrderPlan][Stock] Edit customer product stock');

export const openFertilizerProposalDialog = createAction(
  '[ManualCrops][OrderPlan][Fertilizer] Open fertilizer proposal dialog',
);

export const openConfirmFertilizerOrderDialog = createAction(
  '[ManualCrops][OrderPlan][Fertilizer] Open confirm fertilizer order dialog',
);

export const saveCustomerProductStock = createAction(
  '[ManualCrops][OrderPlan][Stock] Save customer product stock',
  props<{ result: ProductStockData[] }>(),
);

export const autoSaveCustomerProductStock = createAction(
  '[ManualCrops][OrderPlan][Stock] Auto-save customer product stock',
  props<{ productInventories: ProductStockData[] }>(),
);

export const autoSaveCustomerProductStockSuccess = createAction(
  '[ManualCrops][OrderPlan][Stock] Auto-save customer product stock success',
  props<{ productInventories: ProductStockData[] }>(),
);

export const saveFertilizerStock = createAction(
  '[ManualCrops][OrderPlan][Stock] Save fertilizer stock',
  props<{ cropYear: number; fertilizerStock: FertilizerStockData[] }>(),
);

export const optimizeOrderPlan = createAction('[ManualCrops][OrderPlan][Optimize] Optimize order plan');

export const openOrderPlanSummary = createAction('[ManualCrops][OrderPlan][Summary] Open order plan summary');

export const getCropAids = createAction('[ManualCrops][OrderPlan][CropAids] Get crop aids');

export const getCropAidsByIds = createAction(
  '[ManualCrops][OrderPlan][CropAids] Get crop aids by IDs',
  props<{ cropAidIds: number[] }>(),
);

export const setCropAidPackages = createAction(
  '[ManualCrops][OrderPlan][CropAids] Set crop aid packages',
  props<{ cropAidPackages: CropAidPackage[] }>(),
);

export const setCropAids = createAction(
  '[ManualCrops][OrderPlan][CropAids] Set crop aids',
  props<{ cropAids: CropAid[] }>(),
);

export const [loadGBMOrderLines, loadGBMOrderLinesSuccess, loadGBMOrderLinesFailure] = createHttpActions<
  { shouldOpenOrderPlanSummary: boolean },
  { orderLines: OrderLine[] },
  { message: string }
>('[ManualCrops][OrderPlan] Load GBM order lines');

export const [loadFertilizerOrderLines, loadFertilizerOrderLinesSuccess, loadFertilizerOrderLinesFailure] =
  createHttpActions<{ shouldOpenOrderPlanSummary: boolean }, { orderLines: OrderLine[] }, { message: string }>(
    '[ManualCrops][OrderPlan] Load Fertilizer order lines',
  );

export const [loadFertilizerOrderPlan, loadFertilizerOrderPlanSuccess, loadFertilizerOrderPlanFailure] =
  createHttpActions<{ year: number }, { orderPlan: FertilizerOrderPlanResponse }, { message: string }>(
    '[ManualCrops][OrderPlan] Load Fertilizer order plan',
  );

// Kept for backward compatibility, deprecated
export const [loadOrderLines, loadOrderLinesSuccess, loadOrderLinesFailure] = createHttpActions<
  { shouldOpenOrderPlanSummary: boolean },
  { orderLines: OrderLine[] },
  { message: string }
>('[ManualCrops][OrderPlan] Load order lines');

export const getPackages = createAction('[ManualCrops][OrderPlan][Packages] Get packages');

export const setPackages = createAction(
  '[ManualCrops][OrderPlan][Packages] Set packages',
  props<{ packages: Package[] }>(),
);

export const createOrder = createAction('[ManualCrops][OrderPlan][Order] Create order');

export const createFertilizerOrder = createAction(
  '[ManualCrops][OrderPlan][Order] Create fertilizer order',
  props<{ deliveryDate: Date | null }>(),
);

export const createOrderSuccess = createAction(
  '[ManualCrops][OrderPlan][Order] Create order success',
  props<{ customerId: number; locationId: number }>(),
);

export const createFertilizerOrderSuccess = createAction(
  '[ManualCrops][OrderPlan][Order] Create fertilizer order success',
  props<{ customerId: number; locationId: number }>(),
);

export const [deleteOrderLine, deleteOrderLineSuccess, deleteOrderLineFailure] = createHttpActions<
  { orderLineId: number; year: number },
  { orderLineId: number },
  { message: string }
>('[ManualCrops][OrderPlan] Delete order line');

export const [saveOrderLine, saveOrderLineSuccess, saveOrderLineFailure] = createHttpActions<
  { orderLine: OrderLine },
  { orderLine: OrderLine },
  { message: string }
>('[ManualCrops][OrderPlan] Save order line');

export const [loadSalesOrderPeriods, loadSalesOrderPeriodsSuccess, loadSalesOrderPeriodsFailure] = createHttpActions<
  { year: number },
  { salesOrderPeriods: SalesOrderPeriod[] },
  { message: string }
>('[ManualCrops][OrderPlan] Load sales order periods');
